import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import moment from 'moment'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, ONE_DAY_BEFORE_CHECK_IN, TWO_DAYS_BEFORE_CHECK_IN, THREE_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, SEVEN_DAYS_BEFORE_CHECK_IN, THIRTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN, FORTY_FIVE_DAYS_BEFORE_CHECK_IN, SIXTY_DAYS_BEFORE_CHECK_IN, NINETY_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import ModalContext from 'contexts/ModalContext'
import AlwaysOnOfferPropertyCancellationPolicyModal from './AlwaysOnOfferPropertyCancellationPolicyModal'
import { useAppSelector } from 'hooks/reduxHooks'
import useNonAmendableWording from '../useNonAmendableWording'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy
  checkInDate?: string | moment.Moment
  className?: string
  propertyTimezoneOffset?: number
  hideNonRefundableMessage?: boolean
  textLinkFormat?: boolean
  offerType?: string
  enableNonAmendableWording?: boolean
}

function AlwaysOnOfferPropertyCancellationPolicies(props: Props) {
  const {
    cancellationPolicy,
    checkInDate,
    className,
    propertyTimezoneOffset,
    hideNonRefundableMessage,
    textLinkFormat,
    offerType,
    enableNonAmendableWording = false,
  } = props
  const refundableIcon = offerType === 'rental' ? <SolidShieldCheckIcon /> : <LineRefundIcon />
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const standardDetail = intl.formatMessage({
    id: 'amo5QV9y_AP5uY8FxPTJ',
    defaultMessage: 'Standard bookings can be cancelled with a full refund provided the cancellation is made until check-in. That is cancel by 11:59 pm (local time of property) prior to the day of check-in.',
    description: 'Standard cancellation policy detail for AlwaysOn offers',
  })
  let cancellationPolicyDetail = cancellationPolicy.policyDetail[1] ?? standardDetail

  const days = useMemo(() => {
    switch (cancellationPolicy.type) {
      case 'prior-to-check-in-one-day':
        return ONE_DAY_BEFORE_CHECK_IN
      case 'prior-to-check-in-two-days':
        return TWO_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-three-days':
        return THREE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-seven-days':
        return SEVEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-fourteen-days':
        return FOURTEEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-one-days':
      case 'post-purchase-seven-days':
        return TWENTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-eight-days':
        return TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-thirty-one-days':
        return THIRTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-forty-five-days':
        return FORTY_FIVE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-sixty-days':
        return SIXTY_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-ninety-days':
        return NINETY_DAYS_BEFORE_CHECK_IN
      default:
        return 0
    }
  }, [cancellationPolicy.type])

  let startDate: moment.Moment | undefined
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)
  const purchaseMoment = purchaseDate ? moment(purchaseDate) : moment()

  const startBasedOnCheckIn = checkInDate ? ((propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))) : undefined
  const startBasedOnToday = propertyTimezoneOffset ? purchaseMoment.clone().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicy.type === 'post-purchase-seven-days') {
    if (!startBasedOnCheckIn) {
      startDate = startBasedOnToday
    } else {
      startDate = startBasedOnToday.isBefore(startBasedOnCheckIn) ? startBasedOnToday : startBasedOnCheckIn
    }
  } else {
    startDate = startBasedOnCheckIn
  }

  const refundable = cancellationPolicy.type !== 'non-refundable' && (!startDate || startDate?.isAfter(moment()))
  const canShowNonRefundableLabel = !hideNonRefundableMessage && !refundable && cancellationPolicy.type !== 'hidden-cancellation-policy'
  const {
    nonAmendableDetail,
    nonAmendableLabel,
    showNonAmendableWording,
  } = useNonAmendableWording(enableNonAmendableWording && canShowNonRefundableLabel)
  const nonRefundableDetail = showNonAmendableWording ? nonAmendableDetail : intl.formatMessage({
    id: '_xoOFpz56Py4euZPYvqY',
    defaultMessage: 'Please note that ‘Non-Refundable’ packages are guaranteed at the time of booking and not eligible for a refund or date change. Accordingly, please select your package carefully and take note of any cancellation policy applicable to your purchase.',
    description: 'Non-refundable package disclaimer for AlwaysOn offers',
  })
  if (showNonAmendableWording || (cancellationPolicy.type !== 'non-refundable' && !refundable)) {
    cancellationPolicyDetail = nonRefundableDetail
  }

  const freeCancellationLabel = intl.formatMessage({
    id: 'ANaVPf7q7HFxiJ7GU6R4',
    defaultMessage: 'Free cancellation',
    description: 'Form field label for free cancellation on AlwaysOn offers',
  })
  const nonRefundableLabel = showNonAmendableWording ? nonAmendableLabel : intl.formatMessage({
    id: 'kLpWzQ4Bm5HS-3HfjXp9',
    defaultMessage: 'Non-refundable',
    description: 'Label for non-refundable AlwaysOn offers on the offer page',
  })
  const freeCancellationAvailableLabel = intl.formatMessage({
    id: 'B0BkXbhDJdifrqBIaIkJ',
    defaultMessage: 'Free cancellation available',
    description: 'Label when free cancellation is available on AlwaysOn offers',
  })

  const showModal = useContext(ModalContext)

  const onClickIcon = useCallback(() => {
    showModal(<AlwaysOnOfferPropertyCancellationPolicyModal
      refundable={refundable}
      cancellationPolicyDetail={cancellationPolicyDetail}
      nonAmendableTitle={showNonAmendableWording ? nonAmendableLabel : undefined}
    />)
  }, [showModal, refundable, cancellationPolicyDetail, showNonAmendableWording, nonAmendableLabel])

  return (
    <Clickable onClick={onClickIcon} className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {!hideNonRefundableMessage && (
        <>
          {refundable && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-primary-dark"
                  underline={false}
                  startIcon={refundableIcon}
                >
                  {freeCancellationLabel}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  {!startDate && Boolean(days) && <BodyText variant="medium" as="span">
                    <FormattedMessage
                      id="kjepX-L10w0PykWSSgQe"
                      defaultMessage="<emphasis>Free cancellation</emphasis> {days} days prior to check in (Hotel only)"
                      description="Free cancellation with days prior to check-in for hotel-only"
                      values={{
                        days,
                        emphasis: (chunks: React.ReactNode) => <BodyText variant="medium" weight="bold" as="span" colour="highlight-primary-dark">{chunks}</BodyText>,
                      }}
                    />
                  </BodyText>}
                  {!startDate && !days && <BodyText variant="medium" weight="bold" as="span" colour="highlight-primary-dark">{freeCancellationLabel}</BodyText>}
                  {startDate && <BodyText variant="medium" as="span">
                    <FormattedMessage
                      id="R7EXHxQfaXIF0_kYStNs"
                      defaultMessage="<emphasis>Free cancellation</emphasis> until {date} (Hotel only)"
                      description="Free cancellation with deadline date for hotel-only"
                      values={{
                        date: intlDateFormatter(startDate, INTL_SHORT_DAY_NAME_DAY_MONTH),
                        emphasis: (chunks: React.ReactNode) => <BodyText variant="medium" weight="bold" as="span" colour="highlight-primary-dark">{chunks}</BodyText>,
                      }}
                    />
                  </BodyText>}
                  <MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {canShowNonRefundableLabel && (
            <>
              <BodyText variant="medium" as="span">{nonRefundableLabel}</BodyText>
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
      {hideNonRefundableMessage && refundable && (
        <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-primary-dark"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {freeCancellationAvailableLabel}
            </TextLink>}
          {!textLinkFormat && (
            <>
              {startDate && <BodyText variant="medium" as="span">
                <FormattedMessage
                  id="f0MhsYuunp1bM9zpFERa"
                  defaultMessage="<emphasis>Free cancellation available</emphasis> until {date}"
                  description="Free cancellation available with deadline date in the cancellation flow"
                  values={{
                    date: intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT),
                    emphasis: (chunks: React.ReactNode) => <BodyText variant="medium" weight="bold" as="span" colour="highlight-primary-dark">{chunks}</BodyText>,
                  }}
                />
              </BodyText>}
              {!startDate && <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">
                {freeCancellationAvailableLabel}
              </BodyText>}
              <MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
    </Clickable>
  )
}

export default AlwaysOnOfferPropertyCancellationPolicies
